import { FC, Fragment } from 'react';
import { EdnaWrapper } from '@providers/EdnaProvider';
import * as SelectPrimitive from '@radix-ui/react-select';
import styled from 'styled-components';
import DropdownTrigger from '@components/DropdownTrigger';
import Text from '@components/Text/Text';
import Icon from '../Icon';
import { SelectProps, SelectItemProps } from './types';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 'auto';
`;
const Item = styled(SelectPrimitive.Item)`
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  width: fill-available;
  font-family: 'Inter', sans-serif;
  background-color: ${(props) => props.theme.colors.white};
  &:hover,
  &:focus {
    outline: none;
    &:not([data-disabled]) {
      background-color: ${(props) => props.theme.colors.grey075};
      border-radius: 2px;
    }
  }
  cursor: pointer;
  line-height: 24px;
  padding: 4px 8px;
  color: ${(props) => props.theme.colors.grey900};
  font-size: 14px;
  &[data-disabled] {
    opacity: 0.5;
    cursor: default;
  }
`;

const Content = styled(SelectPrimitive.Content)<{ $maxHeight?: string }>`
  display: flex;
  flex-direction: column;
  background-color: white;
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.1),
    0px 0px 1px 0px rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  box-sizing: border-box;
  width: var(--radix-select-trigger-width);
  max-height: ${(props) =>
    props.$maxHeight || 'var(--radix-select-content-available-height)'};
  padding: 4px;
  border: 1px solid ${(props) => props.theme.colors.grey100};
  z-index: 999999;
`;

const SelectTrigger = styled(DropdownTrigger)`
  &[data-placeholder] {
    color: ${(props) =>
      props.disabled ? props.theme.colors.grey300 : props.theme.colors.grey500};
  }

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

/**
 * Use this to display an item in the dropdown. Use as `Select.Item`.
 * Use this as a child of `Select`.
 */
const SelectItem = ({
  disabled = false,
  children,
  className,
  dataId,
  testId,
  value,
}: SelectItemProps) => {
  return (
    <Item
      disabled={disabled}
      data-id={dataId}
      data-testid={testId}
      className={className}
      value={value}
    >
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator asChild>
        <Icon name="CHECK" width="16px" height="16px" color="grey900" />
      </SelectPrimitive.ItemIndicator>
    </Item>
  );
};

/**
 *
 * Displays a list of options for the user to select from. Displays the selected item in the trigger.
 *
 * The items in the select dropdown should be rendered using the `Select.Item` subcomponent.
 * Pass them in as children to `Select`.
 *
 * You can position the select dropdown with the `side` and `align` props. If you need to fine-tune
 * the position, you can use the `alignOffset` and `sideOffset` props as well.
 *
 * The select dropdown has "collision avoidance" enabled. This means that if the dropdown
 * is opened somewhere on the screen where it would collide with another
 * element or with the boundaries of the page (be hidden) then it will automatically
 * reposition itself so that it remains visible.
 *
 * This dropdown uses the headless [select from radix-ui](https://www.radix-ui.com/primitives/docs/components/select),
 * and more props can be exposed as needed.
 */

const Select: FC<SelectProps> & {
  Item: typeof SelectItem;
} = ({
  id,
  label,
  align = 'start',
  alignOffset,
  children,
  className,
  error,
  value,
  open,
  onOpenChange,
  onValueChange,
  side = 'bottom',
  sideOffset,
  width = '160px',
  placeholder,
  disabled = false,
  ariaLabel,
  portal = false,
  maxHeight,
}) => {
  const ContentWrapper = portal ? SelectPrimitive.Portal : Fragment;

  return (
    <EdnaWrapper>
      <Container className={className}>
        {label && (
          <Text
            htmlFor={id}
            element="label"
            variant="bodySm"
            fontWeight="500"
            color="grey800"
          >
            {label}
          </Text>
        )}
        <SelectPrimitive.Root
          open={open}
          onOpenChange={onOpenChange}
          value={value}
          onValueChange={onValueChange}
          disabled={disabled}
        >
          <SelectPrimitive.Trigger id={id} asChild>
            <SelectTrigger
              aria-label={ariaLabel || label}
              error={Boolean(error)}
              width={width}
            >
              <SelectPrimitive.Value placeholder={placeholder} />
            </SelectTrigger>
          </SelectPrimitive.Trigger>
          <ContentWrapper>
            <Content
              align={align}
              side={side}
              collisionPadding={4}
              alignOffset={alignOffset}
              sideOffset={sideOffset}
              position="popper"
              $maxHeight={maxHeight}
            >
              <SelectPrimitive.Viewport>{children}</SelectPrimitive.Viewport>
            </Content>
          </ContentWrapper>
        </SelectPrimitive.Root>
        {error !== undefined && (
          <Text element="label" variant="bodySm" color="red500">
            {error}
          </Text>
        )}
      </Container>
    </EdnaWrapper>
  );
};

Select.Item = SelectItem;

export default Select;

export { SelectItem as Item }; // this export is used for Storybook only. Without these the subcomponents won't show props in the controls panel.
